import { useEffect, useState } from "react";
import { Download, Share, X, Plus } from "lucide-react";
import { Button } from "./ui/button";

const isStandalone = () =>
  (typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true));

const isIOS = () =>
  typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;

const DISMISS_KEY = "pf_install_dismissed";
const HIDDEN_PREFIXES = ["/manage", "/dashboard", "/admin", "/site-admin"];

export function InstallAppPrompt() {
  const [show, setShow] = useState(false);
  const [deferred, setDeferred] = useState(null);
  const [ios, setIos] = useState(false);
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    const check = () => {
      const path = window.location.pathname || "";
      const onCheckout = path.endsWith("/checkout") || path.startsWith("/pay");
      const locked = document.body.hasAttribute("data-scroll-locked") ||
        document.body.style.pointerEvents === "none";
      setBlocked(onCheckout || locked);
    };
    check();
    const obs = new MutationObserver(check);
    obs.observe(document.body, { attributes: true, attributeFilter: ["data-scroll-locked", "style"] });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem(DISMISS_KEY)) return;
    const path = window.location.pathname || "";
    if (HIDDEN_PREFIXES.some((p) => path.startsWith(p))) return;
    const onBIP = (e) => { e.preventDefault(); setDeferred(e); setShow(true); };
    window.addEventListener("beforeinstallprompt", onBIP);
    const onInstalled = () => { setShow(false); localStorage.setItem(DISMISS_KEY, "1"); };
    window.addEventListener("appinstalled", onInstalled);
    const isIos = isIOS();
    setIos(isIos);
    // iOS non emette beforeinstallprompt: mostra le istruzioni dopo un attimo.
    const t = isIos ? setTimeout(() => setShow(true), 1200) : null;
    return () => {
      if (t) clearTimeout(t);
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      try { await deferred.userChoice; } catch (_) { /* user dismissed */ }
      setDeferred(null);
      setShow(false);
      localStorage.setItem(DISMISS_KEY, "1");
    }
  };

  const dismiss = () => { setShow(false); localStorage.setItem(DISMISS_KEY, "1"); };

  if (!show || blocked) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[100] p-3 sm:p-4 flex justify-center pointer-events-none" data-testid="install-app-prompt">
      <div className="pointer-events-auto w-full max-w-md rounded-2xl border border-[#FFC400]/40 bg-[#141419] shadow-2xl p-4 flex items-start gap-3">
        <span className="w-11 h-11 shrink-0 rounded-xl bg-[#FFC400] grid place-items-center text-[#0B0B0D]">
          <Download size={22} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-head font-bold text-white leading-tight">Installa l'app Pickfood</p>
          {ios ? (
            <p className="text-sm text-white/60 mt-0.5">
              Tocca <Share size={13} className="inline mx-0.5 -mt-0.5" /> e poi <span className="whitespace-nowrap font-semibold">“Aggiungi a Home”</span> <Plus size={12} className="inline -mt-0.5" /> per installarla.
            </p>
          ) : (
            <p className="text-sm text-white/60 mt-0.5">Aggiungila alla schermata Home per ordinare più veloce e ricevere le notifiche.</p>
          )}
          {!ios && (
            <Button onClick={install} disabled={!deferred}
              className="mt-3 h-9 rounded-full bg-[#FFC400] hover:bg-[#E6AC00] text-[#0B0B0D] font-semibold" data-testid="install-app-btn">
              Installa app
            </Button>
          )}
        </div>
        <button onClick={dismiss} className="text-white/40 hover:text-white shrink-0 p-1" data-testid="install-app-close" aria-label="Chiudi">
          <X size={18} />
        </button>
      </div>
    </div>
  );
}
